/**
 * Streaming citation markdown transform shared by AGUITransport (Hatch chat)
 * and pairwise ask-stream consumers. Converts `[source](url)` markers into
 * numbered `[[n]](url)` links while buffering incomplete markers across SSE
 * chunks.
 */
const CITATION_FULL_PATTERN = /\[source\]\(([^)[\]]+)\)/;
// eslint-disable-next-line redos/no-vulnerable -- Input is AI-generated text from backend, not user-controlled
const CITATION_PREFIX_PATTERN = /\[(s(o(u(r(c(e(\](\(([^)]*)?)?)?)?)?)?)?)?)?$/;

export class CitationStreamParser {
  private knownSources: Map<string, number> = new Map();
  private citationCounter = 0;
  private textBuffer = '';

  parse(newText: string): string[] {
    const combinedText = this.textBuffer + newText;
    const textChunks: string[] = [];

    this.textBuffer = '';
    let remainingText = combinedText;

    while (remainingText) {
      const match = CITATION_FULL_PATTERN.exec(remainingText);
      if (match) {
        if (match.index > 0) {
          textChunks.push(remainingText.slice(0, match.index));
        }

        const citationUrl = match[1];
        let citationNum: number;

        if (this.knownSources.has(citationUrl)) {
          citationNum = this.knownSources.get(citationUrl)!;
        } else {
          this.citationCounter += 1;
          citationNum = this.citationCounter;
          this.knownSources.set(citationUrl, citationNum);
        }

        textChunks.push(`[[${citationNum}]](${citationUrl})`);
        remainingText = remainingText.slice(match.index + match[0].length);
      } else {
        const partialMatch = CITATION_PREFIX_PATTERN.exec(remainingText);
        if (partialMatch) {
          if (partialMatch.index > 0) {
            textChunks.push(remainingText.slice(0, partialMatch.index));
          }
          this.textBuffer = remainingText.slice(partialMatch.index);
        } else if (remainingText) {
          textChunks.push(remainingText);
        }
        break;
      }
    }

    return textChunks;
  }

  flush(): string {
    const remaining = this.textBuffer;
    this.textBuffer = '';
    return remaining;
  }

  reset(): void {
    this.textBuffer = '';
    this.knownSources.clear();
    this.citationCounter = 0;
  }
}
